/// <amd-module name="CodeCoverage/AllTab/CodeCoverageTreeItemProvider" />

import { ObservableValue, ObservableLike } from "VSS/Core/Observable";
import { ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

import { ICodeCoverageTreeItem } from "CodeCoverage/AllTab/Contracts";
import { PathTreeItemMapper } from "CodeCoverage/AllTab/PathTreeMapper";
import { PathConstants } from "CodeCoverage/ViewCommon/Constants";
import { CoverageSummary } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";

export class CodeCoverageTreeItemProvider extends TreeItemProvider<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> {
    private allTreeItems: { [key: string]: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> };
    private itemsByParentPath: { [parentPath: string]: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>[] };
    private treeMapper: PathTreeItemMapper;

    constructor() {
        super([]);
        this.allTreeItems = {};
        this.itemsByParentPath = {};
        this.treeMapper = new PathTreeItemMapper();
    }

    public replaceAllItems(newItems: (ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>)[]): void {
        this.clear();
        const result = this.treeMapper.createPageTree(newItems);
        this.splice(undefined, undefined, [{ items: result }]);
    }

    public addFirstLevelItems(childrenCodeCoverageData: CoverageSummary[]): void {
        this.addChildItemsInternal(undefined, childrenCodeCoverageData);
    }

    public addChildItems(parentPath: string, childrenCodeCoverageData: CoverageSummary[]): void {
        const parentItem = this.allTreeItems[parentPath];

        if (parentItem) {
            this.addChildItemsInternal(parentItem, childrenCodeCoverageData);
        } else if ($DEBUG) {
            throw new Error(`Parent not found for path: ${parentPath}`);
        }
    }

    public getChildItems = (
        parentPath: string
    ): ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>[] | undefined => {
        const item = this.itemsByParentPath[parentPath];
        if (item) {
            return item;
        }

        return undefined;
    };

    public getAllItems = (): { [key: string]: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> } => {
        return this.allTreeItems;
    };

    private addChildItemsInternal(
        parentTreeItem: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> | undefined,
        childrenCodeCoverageData: CoverageSummary[]
    ): void {
        const childItems = createOneLevelTreeItems(childrenCodeCoverageData);
        for (const item of childItems) {
            if (item.data && !ObservableLike.isObservable(item.data)) {
                const data = item.data as ICodeCoverageTreeItem;
                this.allTreeItems[data.path] = item;
            }
        }
        // Replaces the childItems in the parentTreeItem
        this.splice(parentTreeItem, parentTreeItem ? parentTreeItem.childItems : [], [{ items: childItems }]);

        let parentPath: string = PathConstants.RootPath;
        if (parentTreeItem && !ObservableLike.isObservable(parentTreeItem.data) && (parentTreeItem.data as ICodeCoverageTreeItem).path) {
            parentPath = (parentTreeItem.data as ICodeCoverageTreeItem).path;
        }

        if (parentPath === PathConstants.RootPath) {
            this.itemsByParentPath[parentPath] = childItems;
        } else if (this.allTreeItems[parentPath] && this.allTreeItems[parentPath].childItems) {
            this.itemsByParentPath[parentPath] = this.allTreeItems[parentPath].childItems!;
        }
    }
}

// exported for testing
export function createOneLevelTreeItems(
    items: CoverageSummary[]
): ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>[] {
    const rootItems: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>[] = [];

    for (const item of items) {
        const treeItem: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> = {
            data: convertCoverageSummaryToICodeCoverageTreeItem(item)
        };
        if (item.isDirectory) {
            // Populate a single loading row to show a shimmer while fetching children
            treeItem.childItems = [{ data: new ObservableValue(undefined) } as any];
        }
        rootItems.push(treeItem);
    }

    return rootItems;
}

function convertCoverageSummaryToICodeCoverageTreeItem(item: CoverageSummary): ICodeCoverageTreeItem {
    const totalLines = item.covered + item.notCovered + item.partiallyCovered;
    const coveragePercentage = totalLines != 0 ? (item.covered / totalLines) * 100 : 0;

    return {
        path: item.path,
        coveragePercentage,
        totalLines,
        notCoveredLines: item.notCovered,
        coveredLines: item.covered,
        partiallyCoveredLines: item.partiallyCovered,
        isDirectory: item.isDirectory
    };
}
